'use client';

import { useCallback } from 'react';
import { Adapter } from '@solana/wallet-adapter-base';
import {
	BigNum,
	MarketType,
	PositionDirection,
	getLimitOrderParams,
	getTriggerLimitOrderParams,
	getTriggerMarketOrderParams,
	OrderTriggerCondition,
	PostOnlyParams,
	QUOTE_PRECISION_EXP,
	PRICE_PRECISION_EXP,
	OrderParamsBitFlag,
	MAX_LEVERAGE_ORDER_SIZE,
	ZERO,
	BN,
	User,
	PublicKey,
	QUOTE_SPOT_MARKET_INDEX,
} from '@velocity-exchange/sdk';
import {
	MarketId,
	UIOrderType,
	EMPTY_AUCTION_PARAMS,
	UIMarket,
	getUserKey,
} from '@velocity-exchange/common';
import { SpotTradeFormOutputProps } from 'src/stores/VelocityStore/actions/actionHelpers/orderPrep';
import { ORDER_PREP_UTILS } from 'src/stores/VelocityStore/actions/actionHelpers/orderPrep';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { notify } from 'src/utils/notifications';
import TransactionErrorHandler, {
	AnchorError,
} from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import useVelocityClient from 'src/hooks/useVelocityClient';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import { syncGetCurrentSettings } from 'src/environmentVariables/EnvironmentVariables';
import { safeCheckDevSwitchIsOn } from 'src/hooks/useDevSwitchIsOn';
import { SlippageTolerance } from 'src/@types/types';
import { orderTypeSwitch } from 'src/utils/trade';
import { runOrderActionToast } from 'src/hooks/velocityActions/transactions/runOrderActionToast';
import { dlog } from 'src/dev';
import useAppEventEmitter from 'src/hooks/useAppEventEmitter';
import { OrderFillTimeTracker } from 'src/utils/orderFillTimeTracker';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { updateAfterTx } from 'src/stores/VelocityStore/helpers';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import useGetRawOracleDataForMarket from 'src/hooks/useGetRawOracleDataForMarket';
import { useAutoSignVelocityActionWithFallback } from 'src/hooks/wallets/useAutoSignVelocityActionWithFallback';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import invariant from 'tiny-invariant';
import useCurrentSlotRef from 'src/hooks/useCurrentSlotRef';
import { useUserProtectionSlotClock } from 'src/hooks/useSlotClock';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';

type OpenSpotOrderProps = SpotTradeFormOutputProps & {
	oraclePrice: BN;
};

/**
 * Hook for opening spot orders (both market and non-market orders)
 */
export const useOpenSpotOrder = () => {
	const velocityClient = useVelocityClient();
	const walletAdapter = useVelocityStore((s) => s.wallet.current.adapter);
	const currentUserAccount = useCurrentUserAccount();
	const appEventEmitter = useAppEventEmitter();
	const isGeoblocked = useVelocityStore((s) => s.isGeoblocked);
	const currentSlotRef = useCurrentSlotRef();

	const getTxParams = useGetTxParams();
	const accountsGet = useVelocityAccountsStore((s) => s.get);
	const setVelocityStore = useVelocityStore((s) => s.set);
	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const getRawOracleData = useGetRawOracleDataForMarket();
	const { slotDurationMs } = useUserProtectionSlotClock();

	const openSpotMarketOrder = useCallback(
		async (
			signerWallet: UiEnhancedIWalletV2,
			isAutoSigned: boolean,
			props: SpotTradeFormOutputProps,
			existingToastId?: string
		): Promise<boolean> => {
			if (!velocityClient || !currentUserAccount) return false;

			const marketId = MarketId.createSpotMarket(props.targetMarketIndex);
			let toastId = existingToastId ?? '';

			try {
				const { userAccount, auctionToastEventProps, ...preppedParams } =
					await ORDER_PREP_UTILS.prepSpotMarketOrderParams(props, {
						getUserAccountData: () => {
							return accountsGet().getCurrentUserAccount() as {
								client: User;
								pubKey: PublicKey;
							};
						},
						getVelocityClient: () => velocityClient,
						getPriceInfo: (lookupMarketId: MarketId) => {
							const oraclePrice = getOraclePriceForMarket(lookupMarketId);
							const rawOracle = getRawOracleData(lookupMarketId);
							return {
								markPrice: oraclePrice,
								bestBid: oraclePrice,
								bestAsk: oraclePrice,
								updateSlot: rawOracle?.slot?.toNumber() ?? 0,
							};
						},
						getOraclePriceData: (lookupMarketId: MarketId) =>
							getOraclePriceForMarket(lookupMarketId).val,
						slotDuration: slotDurationMs,
					});
				toastId = toastId || auctionToastEventProps.toastId;

				await velocityClient.switchActiveUser(
					userAccount.subAccountId,
					userAccount.authority
				);

				const auctionVersion =
					useFeatureFlagStore.getState().flags.AUCTION_PARAMS_VERSION.version;

				dlog(
					`stuck_toasts`,
					`placing_spot_market_order_toast .. toastId : ${toastId}`
				);

				appEventEmitter.emit('expectedAuctionInit', {
					...auctionToastEventProps,
					isAutoSigned,
				});

				const txParams = getTxParams({ marketId });

				const spotOrderIx = await velocityClient.getPlaceSpotOrderIx(
					preppedParams.orderParams,
					undefined,
					{
						authority: signerWallet.publicKey,
					}
				);

				if (safeCheckDevSwitchIsOn()) {
					console.log(
						'[useOpenSpotOrder] market order params',
						preppedParams.orderParams
					);
				}

				const txSig = await signerWallet.signAndSendTransaction(
					spotOrderIx,
					txParams
				);

				OrderFillTimeTracker.trackOrderSubmission(
					preppedParams.nextOrderId,
					Date.now(),
					preppedParams.orderParams,
					'legacy',
					preppedParams.userAccountPublicKey.toString(),
					auctionVersion,
					undefined,
					preppedParams.oraclePrice
				);

				appEventEmitter.emit('expectedAuctionTxSigned', {
					...auctionToastEventProps,
					signedTxData: [],
				});

				appEventEmitter.emit(
					'expectedAuctionTxConfirmed',
					auctionToastEventProps
				);

				console.log(`Sent market order with txSig : ${txSig}`);

				setVelocityStore((s) => {
					// @ts-ignore
					s.tradeForm.baseSizeStringValue = '';
					// @ts-ignore
					s.tradeForm.quoteSizeStringValue = '';
				});

				dlog(`stuck_toasts`, `order_confirmed`);
				appEventEmitter.emit('tradeConfirmation');

				const userKey = getUserKey(
					userAccount.subAccountId,
					userAccount.authority
				);

				const accountsState = accountsGet();
				const userAccountClient = accountsState.accounts[userKey]?.client;
				if (userAccountClient) {
					updateAfterTx(velocityClient, userAccountClient);
				}

				return true;
			} catch (e: any) {
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter as any,
				});
				return false;
			}
		},
		[
			velocityClient,
			walletAdapter,
			appEventEmitter,
			currentUserAccount,
			getTxParams,
			accountsGet,
			setVelocityStore,
			getOraclePriceForMarket,
			getRawOracleData,
			slotDurationMs,
		]
	);

	const openSpotNonMarketOrder = useCallback(
		async (
			signerWallet: UiEnhancedIWalletV2,
			props: OpenSpotOrderProps,
			existingToastId?: string
		): Promise<boolean> => {
			const {
				side,
				baseSizeStringValue,
				orderType,
				priceBoxStringValue,
				secondaryPriceBoxStringValue,
				reduceOnly,
				slippageTolerance,
				postOnly,
				targetMarketIndex,
				immediateOrCancel,
				currentPositionDirection,
				oraclePrice,
				maxLeverageSelected,
				spotMarketAccount,
			} = props;

			const marketId = MarketId.createSpotMarket(targetMarketIndex);
			if (
				!baseSizeStringValue ||
				!orderType ||
				!velocityClient ||
				!currentUserAccount
			)
				return false;

			const toastId = existingToastId || `${orderType}-${side}-${Date.now()}`;

			const result = await runOrderActionToast({
				toastId,
				run: async () => {
					const basePrecisionToUse =
						UIMarket.spotMarkets[targetMarketIndex].precisionExp;
					const stepSizeBn = spotMarketAccount.orderStepSize;
					const baseSizeBn = parseInputBigNumOrZero(
						baseSizeStringValue,
						basePrecisionToUse
					).val;
					const baseSizeAfterStep = baseSizeBn.div(stepSizeBn).mul(stepSizeBn);
					const baseSize = BigNum.from(baseSizeAfterStep, basePrecisionToUse);

					// A price box holds the tick's digits wherever there is a tick to
					// mask against, and the parse is exact there. The no-tick fallback
					// renders significant digits instead, so a box can carry more
					// fraction digits than the precision, and the parse truncates them
					// as the float product did.
					const primaryPriceBN = parseInputBigNumOrZero(
						priceBoxStringValue ?? '0',
						PRICE_PRECISION_EXP
					).val;
					const secondaryPriceBN = parseInputBigNumOrZero(
						secondaryPriceBoxStringValue ?? '0',
						PRICE_PRECISION_EXP
					).val;

					const currentPositionIsShort = currentPositionDirection === 'short';
					const targetDirection =
						side === 'buy' ? PositionDirection.LONG : PositionDirection.SHORT;

					let useMaxLeverageFlag = false;
					if (maxLeverageSelected && currentUserAccount?.client) {
						// Check max leverage flag usage
						const tradeQuoteSize = baseSize
							.shiftTo(PRICE_PRECISION_EXP)
							.mul(
								parseInputBigNumOrZero(
									priceBoxStringValue ??
										BigNum.from(oraclePrice, PRICE_PRECISION_EXP).print(),
									PRICE_PRECISION_EXP
								)
							)
							.shiftTo(QUOTE_PRECISION_EXP);
						const userMaxTradeSizeUsdc = BigNum.from(
							currentUserAccount.client.getMaxTradeSizeUSDCForSpot(
								targetMarketIndex,
								targetDirection
							),
							QUOTE_PRECISION_EXP
						);

						const onePercent = userMaxTradeSizeUsdc.scale(1, 100);
						useMaxLeverageFlag = userMaxTradeSizeUsdc
							.sub(tradeQuoteSize)
							.abs()
							.lt(onePercent);
					}

					const orderBaseSize = useMaxLeverageFlag
						? MAX_LEVERAGE_ORDER_SIZE
						: baseSize.val;

					const inputPrice = parseInputBigNumOrZero(
						priceBoxStringValue ??
							BigNum.from(oraclePrice, PRICE_PRECISION_EXP).print(),
						PRICE_PRECISION_EXP
					);

					const settings = syncGetCurrentSettings();
					if (!settings) throw new Error('Settings not found');

					const limitAuctionParams = postOnly
						? EMPTY_AUCTION_PARAMS
						: await ORDER_PREP_UTILS.getLimitOrderAuctionParams({
								marketId,
								limitPrice: inputPrice,
								direction: targetDirection,
								harnessProps: {
									getUserAccountData: () => {
										return accountsGet().getCurrentUserAccount() as {
											client: User;
											pubKey: PublicKey;
										};
									},
									getVelocityClient: () => velocityClient,
									getPriceInfo: (lookupMarketId: MarketId) => {
										const lookupOraclePrice =
											getOraclePriceForMarket(lookupMarketId);
										const rawOracle = getRawOracleData(lookupMarketId);
										return {
											markPrice: lookupOraclePrice,
											bestBid: lookupOraclePrice,
											bestAsk: lookupOraclePrice,
											updateSlot: rawOracle?.slot?.toNumber() ?? 0,
										};
									},
									getOraclePriceData: (lookupMarketId: MarketId) =>
										getOraclePriceForMarket(lookupMarketId).val,
									slotDuration: slotDurationMs,
								},
								amount: orderBaseSize,
								maxLeverageSelected: useMaxLeverageFlag,
								reduceOnly: reduceOnly ?? false,
								slippageTolerance: slippageTolerance as SlippageTolerance,
							});

					const orderParams = orderTypeSwitch<Exclude<UIOrderType, 'market'>>(
						orderType as Exclude<UIOrderType, 'market'>,
						{
							limit: getLimitOrderParams({
								marketIndex: targetMarketIndex,
								marketType: MarketType.SPOT,
								direction: targetDirection,
								baseAssetAmount: orderBaseSize,
								price: primaryPriceBN,
								reduceOnly,
								postOnly: postOnly
									? PostOnlyParams.MUST_POST_ONLY
									: PostOnlyParams.NONE,
								bitFlags: immediateOrCancel
									? OrderParamsBitFlag.ImmediateOrCancel
									: undefined,
								...limitAuctionParams,
							}),
							stopMarket: getTriggerMarketOrderParams({
								marketIndex: targetMarketIndex,
								marketType: MarketType.SPOT,
								direction: targetDirection,
								baseAssetAmount: orderBaseSize,
								triggerPrice: primaryPriceBN,
								triggerCondition: currentPositionIsShort
									? OrderTriggerCondition.ABOVE
									: OrderTriggerCondition.BELOW,
								reduceOnly,
							}),
							stopLimit: getTriggerLimitOrderParams({
								marketIndex: targetMarketIndex,
								marketType: MarketType.SPOT,
								direction: targetDirection,
								baseAssetAmount: orderBaseSize,
								triggerPrice: primaryPriceBN,
								price: secondaryPriceBN,
								triggerCondition: currentPositionIsShort
									? OrderTriggerCondition.ABOVE
									: OrderTriggerCondition.BELOW,
								reduceOnly,
							}),
							takeProfitMarket: getTriggerMarketOrderParams({
								marketIndex: targetMarketIndex,
								marketType: MarketType.SPOT,
								direction: targetDirection,
								baseAssetAmount: orderBaseSize,
								triggerPrice: primaryPriceBN,
								triggerCondition: currentPositionIsShort
									? OrderTriggerCondition.BELOW
									: OrderTriggerCondition.ABOVE,
								reduceOnly,
							}),
							takeProfitLimit: getTriggerLimitOrderParams({
								marketIndex: targetMarketIndex,
								marketType: MarketType.SPOT,
								direction: targetDirection,
								baseAssetAmount: orderBaseSize,
								triggerPrice: primaryPriceBN,
								price: secondaryPriceBN,
								triggerCondition: currentPositionIsShort
									? OrderTriggerCondition.BELOW
									: OrderTriggerCondition.ABOVE,
								reduceOnly,
							}),
							oracleLimit: getLimitOrderParams({
								marketIndex: targetMarketIndex,
								marketType: MarketType.SPOT,
								direction: targetDirection,
								baseAssetAmount: orderBaseSize,
								price: ZERO,
								reduceOnly,
								postOnly: postOnly
									? PostOnlyParams.MUST_POST_ONLY
									: PostOnlyParams.NONE,
								bitFlags: immediateOrCancel
									? OrderParamsBitFlag.ImmediateOrCancel
									: undefined,
								oraclePriceOffset: primaryPriceBN,
							}),
							// these are routed through the market orders function
							oracle: undefined,
							// scaled orders not support for spot yet
							scaledOrders: undefined,
						}
					);

					if (safeCheckDevSwitchIsOn()) {
						console.log(
							'[useOpenSpotOrder] non-market order params',
							orderParams
						);
					}

					const spotOrderIx = await velocityClient.getPlaceSpotOrderIx(
						orderParams,
						undefined,
						{
							authority: signerWallet.publicKey,
						}
					);

					await signerWallet.signAndSendTransaction(
						spotOrderIx,
						getTxParams({ marketId })
					);

					// follows internal implementation of VelocityClient.placeSpotOrder()
					velocityClient.spotMarketLastSlotCache.set(
						targetMarketIndex,
						currentSlotRef.current
					);
					velocityClient.spotMarketLastSlotCache.set(
						QUOTE_SPOT_MARKET_INDEX,
						currentSlotRef.current
					);

					return true;
				},
				success: {
					order: { orderType: orderType as Exclude<UIOrderType, 'market'> },
				},
				handleError: (error, id) =>
					TransactionErrorHandler.handleError({
						error: error as AnchorError,
						toastId: id,
						wallet: walletAdapter as Adapter,
					}),
			});

			return result ?? false;
		},
		[
			velocityClient,
			walletAdapter,
			currentUserAccount,
			getTxParams,
			accountsGet,
			getOraclePriceForMarket,
			getRawOracleData,
			slotDurationMs,
		]
	);

	const openSpotOrder = useAutoSignVelocityActionWithFallback(
		useCallback(
			async (
				signerWallet: UiEnhancedIWalletV2,
				isAutoSigned: boolean,
				toastId: string,
				props: OpenSpotOrderProps
			): Promise<boolean> => {
				invariant(velocityClient, 'driftClient is required');

				if (!currentUserAccount) return false;

				const { baseSizeStringValue, orderType } = props;
				if (!baseSizeStringValue || !orderType) return false;

				if (isGeoblocked && !props.reduceOnly) {
					notify({
						type: 'error',
						message: 'Geoblocked',
						description:
							'You are not allowed to use Velocity from a restricted territory',
						lengthMs: 5000,
					});
					return false;
				}

				// Handle market orders
				if (orderType === 'market') {
					return await openSpotMarketOrder(
						signerWallet,
						isAutoSigned,
						props,
						toastId
					);
				}

				// Handle non-market orders (limit, stop, etc.)
				return await openSpotNonMarketOrder(signerWallet, props, toastId);
			},
			[
				isGeoblocked,
				velocityClient,
				currentUserAccount,
				openSpotMarketOrder,
				openSpotNonMarketOrder,
			]
		)
	);

	return openSpotOrder;
};
